/**
 * 파일 기반 동기화 오케스트레이션 — 결정 #20(+#8 토대). OAuth/네트워크 없이도
 * "동기화 상태를 파일로 주고받아 **병합**"한다. 백업(restoreBackup)이 단순 덮어쓰기인
 * 반면, 이쪽은 core `runSync`로 3-way 충돌을 판정해 **침묵의 데이터 손실 없이** 합친다.
 *
 * 구성(결정 #10/#20):
 *  - core `runSync(local, backend, onConflict)` — 오케스트레이션(이미 검증됨).
 *  - `WebSyncLocal` — IndexedDB 위 로컬 어댑터(메타 보존 raw write).
 *  - `DocumentSyncBackend` — 직렬화 가능한 문서 백엔드(파일 본문). 구글 드라이브(#8)는
 *    이 문서를 appDataFolder 파일에 싣는 전송만 갈아끼우면 된다.
 *
 * 에셋 바이트(결정 #18): 엔티티(Asset)는 hash만 들고 다니므로, 레코드 동기화와 별개로
 * 콘텐츠 주소화 blob을 "없는 것만" 함께 전송한다(여기서 명시적으로 처리).
 */
import {
  DocumentSyncBackend,
  RemoteObjectStoreSyncBackend,
  isSyncDocument,
  runSync,
  type ConflictResolution,
  type RemoteObjectStore,
  type SyncBackend,
  type SyncConflict,
  type SyncProgress,
} from "@roqeet/core";
import { storage } from "./storage.js";
import { WebSyncLocal } from "../platform/sync/web-sync-local.js";
import {
  GoogleDriveClient,
  GoogleDriveSyncBackend,
} from "../platform/sync/google-drive-backend.js";
import { identity } from "./identity.js";

/** 충돌 일괄 처리 전략(파일 동기화 UI용). */
export type ConflictStrategy = "keep-local" | "keep-remote";

export interface SyncOutcome {
  pushed: number;
  pulled: number;
  conflicts: number;
}

/**
 * 살아 있는 로컬 Asset의 blob 중 백엔드에 없는 것을 올린다(결정 #18, 없는 것만).
 * 파일·Drive 동기화 공통.
 */
async function pushLocalAssets(backend: SyncBackend): Promise<void> {
  const assets = await storage.assets.list();
  for (const asset of assets) {
    if (await backend.hasBlob(asset.hash)) continue;
    const blob = await storage.getAssetBlob(asset.hash);
    if (blob) await backend.pushBlob(asset.hash, blob.data);
  }
}

/**
 * 로컬 Asset이 참조하는 blob 중 로컬에 없는 것을 백엔드에서 받아 채운다(결정 #18).
 * 파일·Drive 동기화 공통(병합 후 호출).
 */
async function pullMissingAssets(backend: SyncBackend, local: WebSyncLocal): Promise<void> {
  const assets = await storage.assets.list();
  for (const asset of assets) {
    if (await local.hasBlob(asset.hash)) continue;
    const blob = await backend.pullBlob(asset.hash);
    if (blob) await local.putBlob(asset.hash, blob);
  }
}

/**
 * 현재 로컬 상태 전체를 동기화 문서(JSON 문자열)로 내보낸다. 빈 백엔드에 대해
 * runSync를 돌려 모든 레코드를 push하고(베이스 매니페스트도 현재 상태로 갱신),
 * 살아 있는 에셋 바이트를 함께 담는다.
 */
export async function exportSyncDocument(
  onProgress?: (p: SyncProgress) => void,
): Promise<string> {
  const local = new WebSyncLocal();
  const backend = new DocumentSyncBackend();

  // 빈 원격 → 모든 로컬 레코드가 push된다(충돌 없음).
  await runSync(local, backend, async () => new Map(), onProgress);

  // 에셋 바이트(결정 #18): 살아 있는 Asset의 blob을 문서에 담는다(콘텐츠 주소화 dedup).
  await pushLocalAssets(backend);

  return JSON.stringify(backend.toDocument());
}

/** 동기화 문서를 파일로 내려받는다(브라우저). */
export async function downloadSyncDocument(): Promise<void> {
  const json = await exportSyncDocument();
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  a.href = url;
  a.download = `roqeet-sync-${ts}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * 동기화 문서 파일을 가져와 로컬과 **병합**한다(결정 #20). 단순 덮어쓰기가 아니라
 * 3-way 충돌을 판정하며, 진짜 충돌은 strategy로 일괄 해소한다(사용자가 명시 선택 →
 * 침묵의 손실 아님). 에셋 바이트도 없는 것만 받아온다(결정 #18).
 *
 * @param strategy 충돌 시 어느 쪽을 채택할지("keep-local"=내 변경 우선,
 *                 "keep-remote"=가져온 파일 우선).
 */
export async function importSyncDocument(
  file: Blob,
  onConflicts?: (conflicts: SyncConflict[]) => Promise<Map<string, ConflictResolution>>,
  onProgress?: (p: SyncProgress) => void,
): Promise<SyncOutcome> {
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("동기화 파일을 해석할 수 없습니다 (JSON 오류).");
  }
  if (!isSyncDocument(parsed)) {
    throw new Error("유효한 RoQeet Launcher 동기화 파일이 아닙니다.");
  }

  const backend = new DocumentSyncBackend(parsed);
  const local = new WebSyncLocal();

  const resolveConflicts = onConflicts ?? (async (conflicts: SyncConflict[]) => {
    return new Map(conflicts.map(c => [c.id, "keep-local" as ConflictResolution]));
  });

  const result = await runSync(local, backend, resolveConflicts, onProgress);

  // 에셋 바이트(결정 #18): 병합 후 로컬 Asset이 참조하는 blob 중 없는 것을 문서에서
  // 받아 채운다. 콘텐츠 주소화라 중복 저장은 자동 방지된다.
  await pullMissingAssets(backend, local);

  return result;
}

/**
 * Google Drive와 동기화한다(결정 #20/#8). 본인 Drive appDataFolder의 단일 파일을
 * pull→merge→push한다. 파일 동기화와 동일한 병합 엔진(runSync)을 쓰되 전송만
 * Drive로 교체된다. 우리 서버는 데이터를 보지 않는다.
 *
 * 사전조건: Google 연결(identity.connectGoogle)로 access token이 있어야 한다.
 *
 * @param strategy 충돌 시 우선순위(keep-local=내 변경, keep-remote=드라이브).
 */
export async function syncWithGoogleDrive(
  onConflicts?: (conflicts: SyncConflict[]) => Promise<Map<string, ConflictResolution>>,
  onProgress?: (p: SyncProgress) => void,
): Promise<SyncOutcome> {
  const token = await identity.getValidAccessToken();
  if (!token) {
    throw new Error("Google에 연결되어 있지 않습니다. 먼저 Google 계정을 연결하세요.");
  }

  const driveClient = new GoogleDriveClient(token);
  const backend = new GoogleDriveSyncBackend(driveClient);
  await backend.load(); // Drive에서 기존 문서를 받아온다(없으면 빈 문서).

  const local = new WebSyncLocal();
  const resolveConflicts = onConflicts ?? (async (conflicts: SyncConflict[]) => {
    return new Map(conflicts.map(c => [c.id, "keep-local" as ConflictResolution]));
  });

  const result = await runSync(local, backend, resolveConflicts, onProgress);

  // 에셋 바이트: 로컬→백엔드(없는 것만) + 백엔드→로컬(없는 것만), 양방향(결정 #18).
  await pushLocalAssets(backend);
  await pullMissingAssets(backend, local);

  // 병합 + 에셋이 반영된 문서를 Drive 파일에 다시 업로드.
  await backend.flush();

  // 마지막 동기화 시각 기록(표시용).
  try {
    const settings = await storage.getSettings();
    if (settings) {
      await storage.putSettings({
        ...settings,
        sync: { ...settings.sync, lastSyncedAt: Date.now() },
      });
    }
  } catch {
    /* 표시용이라 실패해도 무시 */
  }

  return result;
}

/**
 * Sync with any direct provider object store. Provider adapters must talk
 * browser/app -> provider directly; RoQeet servers should only keep metadata.
 */
export async function syncWithRemoteObjectStore(
  store: RemoteObjectStore,
  onConflicts?: (conflicts: SyncConflict[]) => Promise<Map<string, ConflictResolution>>,
  onProgress?: (p: SyncProgress) => void,
): Promise<SyncOutcome> {
  const backend = new RemoteObjectStoreSyncBackend(store);
  await backend.load();

  const local = new WebSyncLocal();
  const resolveConflicts = onConflicts ?? (async (conflicts: SyncConflict[]) => {
    return new Map(conflicts.map(c => [c.id, "keep-local" as ConflictResolution]));
  });

  const result = await runSync(local, backend, resolveConflicts, onProgress);
  await pushLocalAssets(backend);
  await pullMissingAssets(backend, local);
  await backend.flush();

  try {
    const settings = await storage.getSettings();
    if (settings) {
      await storage.putSettings({
        ...settings,
        sync: { ...settings.sync, lastSyncedAt: Date.now() },
      });
    }
  } catch {
    /* ignore timestamp failures */
  }

  return result;
}
